import type { MouseEvent } from 'react'
import { useCallback, useRef, useState } from 'react'
import { Trash2, Loader2 } from 'lucide-react'
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from 'date-fns'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { ConversationSummary } from '@/types'

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const minutes = differenceInMinutes(now, date)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min`
  const hours = differenceInHours(now, date)
  if (hours < 24) return `${hours} h`
  const days = differenceInDays(now, date)
  if (days < 7) return `${days} d`
  if (days < 30) return `${Math.floor(days / 7)} w`
  if (days < 365) return `${Math.floor(days / 30)} mo`
  return `${Math.floor(days / 365)} y`
}

const SKELETON_KEYS = Array.from({ length: 20 }, (_, i) => `skeleton-${i}`)

function stopPropagation(e: MouseEvent) {
  e.stopPropagation()
}

interface ConversationHistoryItemProps {
  conv: ConversationSummary
  isActive: boolean
  onConversationClick: (convId: string) => void
  onDeleteConversation: (convId: string) => Promise<void>
}

function ConversationHistoryItem({
  conv,
  isActive,
  onConversationClick,
  onDeleteConversation,
}: ConversationHistoryItemProps) {
  const [open, setOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  // Ref latch: `isDeleting` state doesn't update between two synchronous
  // clicks, so a fast double-click would fire the delete twice. The ref does.
  const isDeletingRef = useRef(false)

  const handleClick = useCallback(() => {
    onConversationClick(conv.id)
  }, [onConversationClick, conv.id])

  const handleDelete = useCallback(
    async (e: MouseEvent) => {
      // Prevent Radix's default close-on-click so the dialog stays open with a
      // loading state until the delete resolves and the row is removed.
      e.preventDefault()
      if (isDeletingRef.current) return
      isDeletingRef.current = true
      setIsDeleting(true)
      try {
        await onDeleteConversation(conv.id)
        setOpen(false)
      } catch {
        // Keep the dialog open so the user can retry.
      } finally {
        isDeletingRef.current = false
        setIsDeleting(false)
      }
    },
    [onDeleteConversation, conv.id]
  )

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // Don't allow Escape/overlay to dismiss mid-delete.
      if (isDeleting) return
      setOpen(next)
    },
    [isDeleting]
  )

  return (
    <SidebarMenuItem className="min-w-0">
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          onClick={handleClick}
          className={cn(
            'group relative overflow-hidden flex items-center gap-1 min-w-0 h-9 rounded-xl px-2',
            'text-sm text-primary-high-contrast',
            'hover:bg-white/40',
            'data-[active=true]:bg-white data-[active=true]:hover:bg-white'
          )}>
          <div>
            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm">
              {conv.title}
            </span>

            {conv.run_status === 'running' ? (
              <Loader2
                aria-label="Still working"
                className="shrink-0 size-3.5 animate-spin text-muted-foreground group-hover:opacity-0 transition-opacity"
              />
            ) : (
              <small className="shrink-0 inline-flex items-center whitespace-nowrap text-xs text-muted-foreground group-hover:opacity-0 transition-opacity">
                {formatRelativeTime(conv.updated_at)}
              </small>
            )}

            <AlertDialogTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={stopPropagation}
                title="Delete conversation"
                aria-label="Delete conversation"
                className="absolute right-2 size-6 shrink-0 opacity-0 group-hover:opacity-100 p-1 hover:bg-destructive/10 rounded transition-opacity">
                <Trash2 className="size-3.5" />
              </Button>
            </AlertDialogTrigger>
          </div>
        </SidebarMenuButton>

        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{conv.title}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Loader2 className="animate-spin" />
                  Deleting…
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarMenuItem>
  )
}

interface ConversationHistoryListProps {
  conversations: ConversationSummary[]
  loading: boolean
  activeConversationId?: string
  searchQuery?: string
  onConversationClick: (convId: string) => void
  onDeleteConversation: (convId: string) => Promise<void>
}

export function ConversationHistoryList({
  conversations,
  loading,
  activeConversationId,
  searchQuery,
  onConversationClick,
  onDeleteConversation,
}: ConversationHistoryListProps) {
  if (loading) {
    return (
      <div
        data-slot="conversation-history-list"
        className="flex-1 min-h-0 mx-3 mb-3 bg-white/65 rounded-xl p-4 overflow-hidden">
        <div className="h-full overflow-y-auto space-y-1">
          {SKELETON_KEYS.map((key) => (
            <div key={key} className="px-2 py-1.5">
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (conversations.length === 0) {
    return (
      <div
        data-slot="conversation-history-list"
        className="flex-1 min-h-0 mx-3 mb-3 bg-white/65 rounded-xl p-4 text-sm text-muted-foreground">
        {searchQuery
          ? `No conversations match "${searchQuery}"`
          : 'No conversations yet'}
      </div>
    )
  }

  return (
    <div
      data-slot="conversation-history-list"
      className="flex-1 min-h-0 mx-3 mb-3 bg-white/65 rounded-xl p-4 overflow-hidden">
      <SidebarMenu className="h-full auto-rows-min gap-1 overflow-y-auto">
        {conversations.map((conv) => (
          <ConversationHistoryItem
            key={conv.id}
            conv={conv}
            isActive={activeConversationId === conv.id}
            onConversationClick={onConversationClick}
            onDeleteConversation={onDeleteConversation}
          />
        ))}
      </SidebarMenu>
    </div>
  )
}
